import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import SearchableSelect from './SearchableSelect';
import RegionCheckboxPicker from './RegionCheckboxPicker';
import ContentPaper from './ContentPaper';
import ConfirmModal from './ConfirmModal';
import FormDialog, { FormSection, FormField, DialogButton } from './FormDialog';
import { IconPlus } from './Icons';
import { useToast } from '../utils/ToastContext';
import {
  CURRENCIES, CURRENCY_CODES, fetchCurrencies, buildCurrenciesMap, buildCurrencyCodes,
} from '../utils/currency';
import useIsMobile from '../hooks/useIsMobile';

const EMPTY_RULE_FORM = {
  source_type: 'any',
  merchant_id: '',
  source_country_id: '',
  source_city_ids: [],
  source_region_ids: [],
  destination_city_ids: [],
  destination_region_ids: [],
  total_rate: '0',
  rider_rate: '0',
  merchant_return_fee: '0',
  is_active: 1,
};

function rateKey(r) {
  return `${r.total_rate}-${r.rider_rate}-${r.company_rate}-${r.merchant_return_fee ?? 0}`;
}

/** Higher value wins when multiple rules match the same route (backend: ORDER BY priority DESC). */
function priorityForSourceType(sourceType) {
  if (sourceType === 'merchant') return 30;
  if (sourceType === 'city') return 15;
  return 5;
}

function groupRules(rules) {
  const groups = {};
  for (const r of rules) {
    const key = rateKey(r);
    if (!groups[key]) {
      groups[key] = {
        total_rate: r.total_rate,
        rider_rate: r.rider_rate,
        company_rate: r.company_rate,
        merchant_return_fee: r.merchant_return_fee ?? 0,
        is_active: r.is_active,
        rules: [],
      };
    }
    groups[key].rules.push(r);
    if (!r.is_active) groups[key].is_active = false;
  }
  return Object.values(groups).sort((a, b) => b.total_rate - a.total_rate || b.rules.length - a.rules.length);
}

export default function ShippingPricingSettings({ onSaved, wrapperStyle, ridersEnabled = true, readOnly = false }) {
  const { addToast } = useToast();
  const isMobile = useIsMobile();
  const canEdit = !readOnly;

  const [currencyList, setCurrencyList] = useState(null);
  const [shippingForm, setShippingForm] = useState({
    currency: 'SAR',
    default_total_rate: '0',
    default_rider_rate: '0',
    merchant_return_fee: '0',
    advanced_shipping_rules_enabled: 0,
  });
  const [allRules, setAllRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState('');
  const [shippingRulesSaving, setShippingRulesSaving] = useState(false);
  const [shippingRulesFilter, setShippingRulesFilter] = useState({ search: '', source_type: '', is_active: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [merchantList, setMerchantList] = useState([]);
  const [countries, setCountries] = useState([]);
  const [sourceCities, setSourceCities] = useState([]);
  const [destinationCities, setDestinationCities] = useState([]);
  const [sourceRegions, setSourceRegions] = useState([]);
  const [destinationRegions, setDestinationRegions] = useState([]);
  const [ruleForm, setRuleForm] = useState(EMPTY_RULE_FORM);
  const [groupForm, setGroupForm] = useState({ total_rate: '0', rider_rate: '0', is_active: 1 });
  const [companyCountryId, setCompanyCountryId] = useState(null);
  const [companyCountryName, setCompanyCountryName] = useState('');
  const [countryDefaultCurrency, setCountryDefaultCurrency] = useState(null);
  const [ready, setReady] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.all([
        loadCurrencies(),
        loadShippingSettings(),
        loadMerchants(),
        loadCompanyCities(),
        loadCountries(),
      ]);
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (ready) loadAllRules();
  }, [ready, shippingRulesFilter]);

  async function loadCurrencies() {
    const list = await fetchCurrencies();
    if (list) setCurrencyList(list);
  }

  async function loadShippingSettings() {
    try {
      const data = await api.getShippingSettings();
      setShippingForm({
        currency: data.currency || data.country_default_currency || 'SAR',
        default_total_rate: String(data.default_total_rate ?? 0),
        default_rider_rate: String(data.default_rider_rate ?? 0),
        merchant_return_fee: String(data.merchant_return_fee ?? 0),
        advanced_shipping_rules_enabled: data.advanced_rules_enabled ? 1 : 0,
      });
      if (data.country_id) setCompanyCountryId(data.country_id);
      if (data.country_name_ar) setCompanyCountryName(data.country_name_ar);
      if (data.country_default_currency) {
        setCountryDefaultCurrency({
          code: data.country_default_currency,
          symbol: data.country_default_currency_symbol || data.country_default_currency,
          name: data.country_name_ar || '',
        });
      }
    } catch (err) {
      console.warn('[ShippingPricingSettings] loadShippingSettings failed:', err);
    }
  }

  async function loadCountries() {
    try {
      const data = await api.getCountries();
      setCountries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('[ShippingPricingSettings] loadCountries failed:', err);
      setCountries([]);
    }
  }

  async function loadSourceCitiesForCountry(countryId) {
    if (!countryId) {
      setSourceCities([]);
      return [];
    }
    try {
      const data = await api.getCities(countryId);
      const cities = Array.isArray(data) ? data : [];
      setSourceCities(cities);
      return cities;
    } catch (err) {
      console.warn('[ShippingPricingSettings] loadSourceCitiesForCountry failed:', err);
      setSourceCities([]);
      return [];
    }
  }

  async function loadCompanyCities() {
    try {
      const data = await api.getShippingCities();
      const countryId = data?.country_id ?? null;
      setCompanyCountryId(countryId);
      setCompanyCountryName(data?.country_name_ar || '');
      const scoped = Array.isArray(data?.cities) ? data.cities : [];
      setDestinationCities(scoped);
    } catch (err) {
      setCompanyCountryId(null);
      setCompanyCountryName('');
      setDestinationCities([]);
      if (err.status !== 400) {
        console.warn('[ShippingPricingSettings] loadCompanyCities failed:', err);
      }
    }
  }

  async function loadMerchants() {
    try {
      const data = await api.getMerchants({ limit: 200 });
      setMerchantList(data.merchants || []);
    } catch (err) {
      console.warn('[ShippingPricingSettings] loadMerchants failed:', err);
    }
  }

  async function loadRegionsForCity(cityId, setter) {
    if (!cityId) { setter([]); return; }
    try {
      const data = await api.getRegions(cityId);
      setter(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('[ShippingPricingSettings] loadRegionsForCity failed:', err);
      setter([]);
    }
  }

  /** Resolve city id for source regions (merchant city or single selected source city). */
  function resolveSourceCityId(form = ruleForm) {
    if (form.source_type === 'city') {
      const ids = (form.source_city_ids || []).map(String);
      return ids.length === 1 ? ids[0] : null;
    }
    if (form.source_type === 'merchant') {
      const merchant = merchantList.find((m) => String(m.id) === String(form.merchant_id));
      return merchant?.city_id || null;
    }
    return null;
  }

  async function createRegionForCity(cityId, nameAr, listSetter) {
    if (!canEdit) return null;
    const region = await api.createShippingRegion({
      city_id: cityId,
      name_ar: nameAr,
      name: nameAr,
    });
    if (region?.id != null) {
      listSetter((prev) => {
        const exists = prev.some((r) => String(r.id) === String(region.id));
        if (exists) {
          return prev.map((r) => (String(r.id) === String(region.id) ? { ...r, ...region } : r))
            .sort((a, b) => String(a.name_ar || a.name).localeCompare(String(b.name_ar || b.name), 'ar'));
        }
        return [...prev, region].sort((a, b) =>
          String(a.name_ar || a.name).localeCompare(String(b.name_ar || b.name), 'ar'),
        );
      });
      const label = region.name_ar || region.name || nameAr;
      addToast(
        region.existing ? `المنطقة «${label}» موجودة وتم اختيارها` : `تمت إضافة المنطقة «${label}»`,
        'success',
      );
    }
    return region;
  }

  async function loadAllRules() {
    setRulesLoading(true);
    setRulesError('');
    try {
      const res = await api.getShippingRules({
        limit: 500,
        search: shippingRulesFilter.search || undefined,
        source_type: shippingRulesFilter.source_type || undefined,
        is_active: shippingRulesFilter.is_active !== '' ? shippingRulesFilter.is_active : undefined,
      });
      setAllRules(res.rules || []);
    } catch (err) {
      setRulesError(err.message || 'فشل تحميل قواعد الشحن');
    } finally {
      setRulesLoading(false);
    }
  }

  async function handleSaveDefaults(e) {
    e.preventDefault();
    if (!canEdit) return;
    setRulesError('');
    setShippingRulesSaving(true);
    try {
      const total = Number(shippingForm.default_total_rate || 0);
      const rider = ridersEnabled ? Number(shippingForm.default_rider_rate || 0) : 0;
      const companyRate = total - rider;
      if (total < 0 || rider < 0) throw new Error('قيم التسعير يجب أن تكون غير سالبة');
      if (ridersEnabled && rider > total) throw new Error('لا يمكن أن تتجاوز تكلفة المندوب إجمالي الشحن');
      if (companyRate < 0) throw new Error('تكلفة الشركة لا يمكن أن تكون سالبة');

      const updated = await api.updateShippingSettings({
        currency: shippingForm.currency,
        default_total_rate: total,
        default_rider_rate: rider,
        merchant_return_fee: Number(shippingForm.merchant_return_fee || 0),
        default_company_rate: companyRate,
        advanced_shipping_rules_enabled: shippingForm.advanced_shipping_rules_enabled ? 1 : 0,
      });
      const snapshot = {
        currency: updated.currency || shippingForm.currency,
        default_total_rate: updated.default_total_rate ?? total,
        default_rider_rate: updated.default_rider_rate ?? rider,
        merchant_return_fee: updated.merchant_return_fee ?? Number(shippingForm.merchant_return_fee || 0),
        default_company_rate: updated.default_company_rate ?? companyRate,
        advanced_rules_enabled: updated.advanced_rules_enabled ? 1 : 0,
      };
      setShippingForm({
        currency: snapshot.currency,
        default_total_rate: String(snapshot.default_total_rate),
        default_rider_rate: String(snapshot.default_rider_rate),
        merchant_return_fee: String(snapshot.merchant_return_fee),
        advanced_shipping_rules_enabled: snapshot.advanced_rules_enabled,
      });
      onSaved?.(snapshot);
      addToast('تم حفظ إعدادات الشحن بنجاح', 'success');
    } catch (err) {
      setRulesError(err.message || 'فشل حفظ إعدادات الشحن');
    } finally {
      setShippingRulesSaving(false);
    }
  }

  async function openRuleModal(rule = null) {
    if (!canEdit) return;
    if (!shippingForm.advanced_shipping_rules_enabled) {
      addToast('فعّل «قواعد الشحن المتقدمة» واحفظ الإعدادات أولاً', 'warning');
      return;
    }
    setRulesError('');
    setEditingRule(rule);
    setModalOpen(true);
    const defaultCountryId = companyCountryId ? String(companyCountryId) : '';
    const nextForm = rule ? {
      source_type: rule.source_type || 'any',
      merchant_id: rule.merchant_id || '',
      source_country_id: defaultCountryId,
      source_city_ids: rule.source_city_id ? [String(rule.source_city_id)] : [],
      source_region_ids: rule.source_region_id ? [String(rule.source_region_id)] : [],
      destination_city_ids: rule.destination_city_id ? [String(rule.destination_city_id)] : [],
      destination_region_ids: rule.destination_region_id ? [String(rule.destination_region_id)] : [],
      total_rate: String(rule.total_rate ?? 0),
      rider_rate: String(rule.rider_rate ?? 0),
      merchant_return_fee: String(rule.merchant_return_fee ?? shippingForm.merchant_return_fee ?? 0),
      is_active: rule.is_active ? 1 : 0,
    } : {
      ...EMPTY_RULE_FORM,
      source_country_id: defaultCountryId,
      total_rate: shippingForm.default_total_rate || '0',
      rider_rate: ridersEnabled ? (shippingForm.default_rider_rate || '0') : '0',
      merchant_return_fee: shippingForm.merchant_return_fee || '0',
    };
    setRuleForm(nextForm);

    if (rule?.source_type === 'city' && rule.source_city_id) {
      try {
        const cityRow = await api.getCity(rule.source_city_id).catch(() => null);
        const countryId = cityRow?.country_id ? String(cityRow.country_id) : defaultCountryId;
        setRuleForm((prev) => ({ ...prev, source_country_id: countryId }));
        await loadSourceCitiesForCountry(countryId);
      } catch {
        await loadSourceCitiesForCountry(defaultCountryId);
      }
    } else if (defaultCountryId) {
      await loadSourceCitiesForCountry(defaultCountryId);
    } else {
      setSourceCities([]);
    }

    const sourceCityForRegions = nextForm.source_type === 'city'
      ? (nextForm.source_city_ids.length === 1 ? nextForm.source_city_ids[0] : null)
      : (nextForm.source_type === 'merchant'
        ? merchantList.find((m) => String(m.id) === String(nextForm.merchant_id))?.city_id
        : null);
    loadRegionsForCity(sourceCityForRegions, setSourceRegions);
    const destCityForRegions = nextForm.destination_city_ids.length === 1
      ? nextForm.destination_city_ids[0]
      : null;
    loadRegionsForCity(destCityForRegions, setDestinationRegions);
  }

  function closeRuleModal() {
    setModalOpen(false);
    setEditingRule(null);
    setSourceRegions([]);
    setDestinationRegions([]);
  }

  async function handleSaveRule(e) {
    e.preventDefault();
    if (!canEdit) return;
    setRulesError('');
    setShippingRulesSaving(true);
    try {
      const payload = {
        source_type: ruleForm.source_type,
        merchant_id: ruleForm.source_type === 'merchant' ? (ruleForm.merchant_id || undefined) : undefined,
        source_city_ids: ruleForm.source_type === 'city' ? ruleForm.source_city_ids : undefined,
        source_region_ids: ruleForm.source_type === 'any'
          ? []
          : (ruleForm.source_city_ids.length === 1 ? ruleForm.source_region_ids : []),
        destination_city_ids: ruleForm.destination_city_ids,
        destination_region_ids: ruleForm.destination_city_ids.length === 1
          ? ruleForm.destination_region_ids
          : [],
        total_rate: Number(ruleForm.total_rate || 0),
        rider_rate: ridersEnabled ? Number(ruleForm.rider_rate || 0) : 0,
        merchant_return_fee: Number(ruleForm.merchant_return_fee || 0),
        priority: priorityForSourceType(ruleForm.source_type),
        is_active: ruleForm.is_active ? 1 : 0,
      };
      if (ruleForm.source_type === 'city' && (!payload.source_city_ids || payload.source_city_ids.length === 0)) {
        throw new Error('اختر مدينة مصدر واحدة على الأقل');
      }
      if (!payload.destination_city_ids || payload.destination_city_ids.length === 0) {
        throw new Error('اختر مدينة وجهة واحدة على الأقل');
      }
      if (editingRule && ruleForm.source_type === 'city' && payload.source_city_ids.length > 1) {
        throw new Error('عند تعديل قاعدة موجودة يمكن اختيار مدينة مصدر واحدة — أنشئ قاعدة جديدة لإضافة مدن أخرى');
      }
      if (editingRule && payload.destination_city_ids.length > 1) {
        throw new Error('عند تعديل قاعدة موجودة يمكن اختيار مدينة وجهة واحدة — أنشئ قاعدة جديدة لإضافة مدن أخرى');
      }
      if (ridersEnabled && payload.rider_rate > payload.total_rate) {
        throw new Error('لا يمكن أن تتجاوز تكلفة المندوب إجمالي الشحن');
      }
      if (payload.merchant_return_fee < 0) {
        throw new Error('رسوم المرتجع يجب أن تكون غير سالبة');
      }
      if (editingRule) {
        await api.updateShippingRule(editingRule.id, payload);
        addToast('تم حفظ قاعدة الشحن بنجاح', 'success');
      } else {
        const res = await api.createShippingRule(payload);
        const count = res?.created_count || 1;
        addToast(
          count > 1 ? `تم إنشاء ${count} قواعد شحن بنجاح` : 'تم حفظ قاعدة الشحن بنجاح',
          'success',
        );
      }
      closeRuleModal();
      await loadAllRules();
    } catch (err) {
      setRulesError(err.message || 'فشل حفظ قاعدة الشحن');
    } finally {
      setShippingRulesSaving(false);
    }
  }

  function openGroupEdit(group) {
    if (!canEdit) return;
    setEditingGroup(group);
    setGroupForm({
      total_rate: String(group.total_rate),
      rider_rate: String(group.rider_rate),
      merchant_return_fee: String(group.merchant_return_fee ?? shippingForm.merchant_return_fee ?? 0),
      is_active: group.is_active ? 1 : 0,
    });
    setRulesError('');
    setGroupModalOpen(true);
  }

  function closeGroupEdit() {
    setGroupModalOpen(false);
    setEditingGroup(null);
  }

  async function handleSaveGroup(e) {
    e.preventDefault();
    if (!canEdit) return;
    const group = editingGroup;
    if (!group || !group.rules.length) return;
    setRulesError('');
    setShippingRulesSaving(true);
    try {
      const total = Number(groupForm.total_rate || 0);
      const rider = ridersEnabled ? Number(groupForm.rider_rate || 0) : 0;
      const returnFee = Number(groupForm.merchant_return_fee || 0);
      if (ridersEnabled && rider > total) throw new Error('لا يمكن أن تتجاوز تكلفة المندوب إجمالي الشحن');
      if (returnFee < 0) throw new Error('رسوم المرتجع يجب أن تكون غير سالبة');

      const ruleIds = group.rules.map((r) => r.id);
      await api.batchUpdateShippingRules({
        rule_ids: ruleIds,
        total_rate: total,
        rider_rate: rider,
        company_rate: total - rider,
        merchant_return_fee: returnFee,
        is_active: groupForm.is_active ? 1 : 0,
      });

      if (groupForm.is_active !== group.is_active) {
        for (const r of group.rules) {
          try { await api.toggleShippingRule(r.id); } catch { /* ignore */ }
        }
      }

      closeGroupEdit();
      await loadAllRules();
      addToast('تم تحديث مجموعة التسعير بنجاح', 'success');
    } catch (err) {
      setRulesError(err.message || 'فشل تحديث المجموعة');
    } finally {
      setShippingRulesSaving(false);
    }
  }

  async function handleToggleGroup(group) {
    if (!canEdit) return;
    const ruleIds = group.rules.map((r) => r.id);
    try {
      const targetState = group.rules.some((r) => r.is_active) ? 0 : 1;
      await api.batchUpdateShippingRules({ rule_ids: ruleIds, is_active: targetState });
      await loadAllRules();
      addToast(targetState ? 'تم تفعيل المجموعة' : 'تم تعطيل المجموعة', 'success');
    } catch (err) {
      setRulesError(err.message || 'فشل تحديث المجموعة');
    }
  }

  function handleDeleteGroup(group) {
    if (!canEdit) return;
    setConfirmDelete({
      title: 'حذف مجموعة التسعير',
      message: `هل تريد حذف ${group.rules.length} قاعدة من هذه المجموعة؟`,
      danger: true,
      onConfirm: async () => {
        try {
          for (const r of group.rules) {
            await api.deleteShippingRule(r.id);
          }
          await loadAllRules();
          addToast('تم حذف المجموعة', 'success');
        } catch (err) {
          setRulesError(err.message || 'فشل حذف المجموعة');
        } finally {
          setConfirmDelete(null);
        }
      },
    });
  }

  async function handleToggleRule(rule) {
    if (!canEdit) return;
    try {
      await api.toggleShippingRule(rule.id);
      await loadAllRules();
    } catch (err) {
      setRulesError(err.message || 'فشل تحديث حالة القاعدة');
    }
  }

  function handleDeleteRule(rule) {
    if (!canEdit) return;
    setConfirmDelete({
      title: 'حذف قاعدة الشحن',
      message: 'هل تريد حذف قاعدة الشحن هذه؟',
      danger: true,
      onConfirm: async () => {
        try {
          await api.deleteShippingRule(rule.id);
          await loadAllRules();
          addToast('تم حذف القاعدة', 'success');
        } catch (err) {
          setRulesError(err.message || 'فشل حذف القاعدة');
        } finally {
          setConfirmDelete(null);
        }
      },
    });
  }

  const currencyOptions = currencyList ? buildCurrencyCodes(currencyList) : CURRENCY_CODES;
  const currencyMap = currencyList ? buildCurrenciesMap(currencyList) : CURRENCIES;
  const curSymbol = currencyMap[shippingForm.currency]?.symbol || shippingForm.currency;
  const advancedOn = !!shippingForm.advanced_shipping_rules_enabled;
  const companyRateDisplay = ridersEnabled
    ? Math.max(0, Number(shippingForm.default_total_rate || 0) - Number(shippingForm.default_rider_rate || 0)).toFixed(2)
    : Number(shippingForm.default_total_rate || 0).toFixed(2);

  const groups = groupRules(allRules);

  return (
    <>
      <ContentPaper style={{ ...wrapperStyle, padding: isMobile ? 16 : 22, gridColumn: '1 / -1' }}>
        <h3 className="vi-settings-section__title">الشحن والتسعير</h3>
        <p className="vi-form-hint" style={{ marginBottom: 16 }}>
          حدّد التسعير الافتراضي للطلبات، ثم أضف قواعد مخصّصة حسب المصدر والوجهة عند الحاجة.
        </p>
        {rulesError && <div className="vi-alert vi-alert--error" style={{ marginBottom: 14 }}>{rulesError}</div>}

        <div className="vi-shipping-block">
          <h4 className="vi-shipping-block__title">١ — التسعير الافتراضي</h4>
          <p className="vi-form-hint">يُطبَّق على كل الطلبات ما لم تُطابق قاعدة متقدمة.</p>
        </div>
        <form onSubmit={handleSaveDefaults} className="vi-shipping-defaults-form">
          <FormField>
            <label className="vi-label">العملة</label>
            <select
              className="vi-input"
              value={shippingForm.currency}
              disabled={!canEdit}
              onChange={(e) => setShippingForm((prev) => ({ ...prev, currency: e.target.value }))}
            >
              {currencyOptions.map((code) => (
                <option key={code} value={code}>{currencyMap[code]?.label || code}</option>
              ))}
            </select>
            {countryDefaultCurrency?.code && (
              <p className="vi-form-hint" style={{ marginTop: 8 }}>
                العملة الافتراضية{countryDefaultCurrency.name ? ` لـ ${countryDefaultCurrency.name}` : ''}:{' '}
                <strong>{countryDefaultCurrency.symbol} ({countryDefaultCurrency.code})</strong>
                {shippingForm.currency !== countryDefaultCurrency.code && (
                  <button
                    type="button"
                    className="vi-btn vi-btn-sm vi-btn-outline"
                    style={{ marginInlineStart: 8 }}
                    onClick={() => setShippingForm((prev) => ({ ...prev, currency: countryDefaultCurrency.code }))}
                    disabled={!canEdit}
                  >
                    استخدام العملة الافتراضية
                  </button>
                )}
              </p>
            )}
          </FormField>
          <div className="vi-shipping-rate-grid">
            <RateField
              label="إجمالي الشحن (للعميل)"
              value={shippingForm.default_total_rate}
              onChange={(v) => setShippingForm((prev) => ({ ...prev, default_total_rate: v }))}
              disabled={!canEdit}
            />
            {ridersEnabled && (
              <RateField
                label="تكلفة المندوب"
                value={shippingForm.default_rider_rate}
                onChange={(v) => setShippingForm((prev) => ({ ...prev, default_rider_rate: v }))}
                disabled={!canEdit}
              />
            )}
            <FormField>
              <label className="vi-label">{ridersEnabled ? 'تكلفة الشركة' : 'إيراد الشركة'}</label>
              <div className="vi-form-locked" dir="ltr">{companyRateDisplay}</div>
            </FormField>
            <RateField
              label="رسوم المرتجع على التاجر"
              value={shippingForm.merchant_return_fee}
              onChange={(v) => setShippingForm((prev) => ({ ...prev, merchant_return_fee: v }))}
              disabled={!canEdit}
            />
          </div>
          <div className={`vi-shipping-toggle${advancedOn ? ' vi-shipping-toggle--on' : ''}`}>
            <label className="vi-shipping-toggle__label">
              <input
                type="checkbox"
                checked={advancedOn}
                onChange={(e) => setShippingForm((prev) => ({
                  ...prev,
                  advanced_shipping_rules_enabled: e.target.checked ? 1 : 0,
                }))}
                disabled={!canEdit}
              />
              <span>تفعيل قواعد الشحن المتقدمة (حسب المصدر والوجهة)</span>
            </label>
          </div>
          <div className="vi-shipping-form-actions">
            <button type="submit" className="vi-btn vi-btn-primary" disabled={readOnly || shippingRulesSaving}>
              {shippingRulesSaving ? 'جاري الحفظ...' : 'حفظ إعدادات الشحن'}
            </button>
          </div>
        </form>

        <div className="vi-shipping-rules-section">
          <div className="vi-shipping-block">
            <h4 className="vi-shipping-block__title">٢ — قواعد التسعير حسب المصدر والوجهة</h4>
          </div>
          {!advancedOn ? (
            <div className="vi-shipping-hint-card">
              <span className="vi-shipping-hint-card__icon">📋</span>
              <div>
                <div className="vi-shipping-hint-card__title">القواعد المتقدمة غير مفعّلة</div>
                <p className="vi-form-hint" style={{ margin: 0 }}>
                  فعّل الخيار أعلاه واحفظ الإعدادات، ثم أضف قواعد لتحديد سعر الشحن حسب مدينة/تاجر المصدر ومدينة/منطقة الوجهة.
                </p>
              </div>
            </div>
            ) : !companyCountryId ? (
              <div className="vi-shipping-hint-card">
                <span className="vi-shipping-hint-card__icon">🌍</span>
                <div>
                  <div className="vi-shipping-hint-card__title">دولة الشركة غير محددة</div>
                  <p className="vi-form-hint" style={{ margin: 0 }}>
                    يجب تحديد دولة الشركة من لوحة المشرف العام لتتمكن من اختيار المدن في قواعد الشحن.
                  </p>
                </div>
              </div>
            ) : (
              <>

            <div className="vi-shipping-rules-toolbar">
                <p className="vi-form-hint" style={{ margin: 0, flex: 1 }}>
                  ترتيب المطابقة: تاجر (الأعلى) ← مدينة+منطقة ← مدينة ← عام — ثم الرجوع للتسعير الافتراضي
                </p>
                {canEdit && <button type="button" className="vi-btn vi-btn-primary vi-btn-sm" onClick={() => openRuleModal(null)}>
                  <IconPlus /> إضافة قاعدة
                </button>}
              </div>
              <div className="vi-filter-bar vi-filter-bar--compact">
                <div className="vi-filter-field">
                  <input
                    className="vi-input"
                    value={shippingRulesFilter.search}
                    onChange={(e) => {
                      setShippingRulesFilter((prev) => ({ ...prev, search: e.target.value }));
                    }}
                    placeholder="بحث في القواعد"
                  />
                </div>
                <div className="vi-filter-field">
                  <select
                    className="vi-input"
                    value={shippingRulesFilter.source_type}
                    onChange={(e) => {
                      setShippingRulesFilter((prev) => ({ ...prev, source_type: e.target.value }));
                    }}
                  >
                    <option value="">كل الأنواع</option>
                    <option value="any">عام</option>
                    <option value="city">مدينة</option>
                    <option value="merchant">تاجر</option>
                  </select>
                </div>
                <div className="vi-filter-field">
                  <select
                    className="vi-input"
                    value={shippingRulesFilter.is_active}
                    onChange={(e) => {
                      setShippingRulesFilter((prev) => ({ ...prev, is_active: e.target.value }));
                    }}
                  >
                    <option value="">كل الحالات</option>
                    <option value="1">مفعلة</option>
                    <option value="0">معطلة</option>
                  </select>
                </div>
              </div>
              <GroupedRulesView
                groups={groups}
                loading={rulesLoading}
                isMobile={isMobile}
                currencySymbol={curSymbol}
                ridersEnabled={ridersEnabled}
                onEdit={openRuleModal}
                onDelete={handleDeleteRule}
                onToggle={handleToggleRule}
                onEditGroup={openGroupEdit}
                onToggleGroup={handleToggleGroup}
                onDeleteGroup={handleDeleteGroup}
                canEdit={canEdit}
              />
            </>
          )}
        </div>
      </ContentPaper>

      {modalOpen && (
        <RuleModal
          isMobile={isMobile}
          editing={editingRule}
          error={rulesError}
          saving={shippingRulesSaving}
          ruleForm={ruleForm}
          setRuleForm={setRuleForm}
          merchantList={merchantList}
          countries={countries}
          sourceCities={sourceCities}
          destinationCities={destinationCities}
          companyCountryId={companyCountryId}
          companyCountryName={companyCountryName}
          sourceRegions={sourceRegions}
          destinationRegions={destinationRegions}
          ridersEnabled={ridersEnabled}
          onSourceCountryChange={loadSourceCitiesForCountry}
          onLoadSourceRegions={(cityId) => loadRegionsForCity(cityId, setSourceRegions)}
          onLoadDestinationRegions={(cityId) => loadRegionsForCity(cityId, setDestinationRegions)}
          onCreateSourceRegion={async (name) => {
            const cityId = resolveSourceCityId();
            if (!cityId) throw new Error('اختر المصدر أولاً');
            return createRegionForCity(cityId, name, setSourceRegions);
          }}
          onCreateDestinationRegion={async (name) => {
            const cityId = ruleForm.destination_city_ids?.length === 1
              ? ruleForm.destination_city_ids[0]
              : null;
            if (!cityId) throw new Error('اختر مدينة وجهة واحدة أولاً');
            return createRegionForCity(cityId, name, setDestinationRegions);
          }}
          onClose={closeRuleModal}
          onSubmit={handleSaveRule}
        />
      )}

      {groupModalOpen && editingGroup && (
        <GroupEditModal
          group={editingGroup}
          form={groupForm}
          setForm={setGroupForm}
          saving={shippingRulesSaving}
          error={rulesError}
          ridersEnabled={ridersEnabled}
          onClose={closeGroupEdit}
          onSubmit={handleSaveGroup}
          isMobile={isMobile}
        />
      )}

      <ConfirmModal
        open={!!confirmDelete}
        onClose={() => !shippingRulesSaving && setConfirmDelete(null)}
        onConfirm={confirmDelete?.onConfirm || (() => {})}
        title={confirmDelete?.title}
        message={confirmDelete?.message || ''}
        danger={confirmDelete?.danger}
        loading={shippingRulesSaving}
        confirmText="حذف"
      />
    </>
  );
}

function RateField({ label, value, onChange, disabled = false }) {
  return (
    <FormField>
      <label className="vi-label">{label}</label>
      <input
        type="number"
        min="0"
        step="0.01"
        className="vi-input"
        dir="ltr"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '' || (!isNaN(Number(v)) && Number(v) >= 0)) onChange(v);
        }}
      />
    </FormField>
  );
}

function GroupedRulesView({ groups, loading, isMobile, currencySymbol, ridersEnabled = true, onEdit, onDelete, onToggle, onEditGroup, onToggleGroup, onDeleteGroup, canEdit = true }) {
  const [expanded, setExpanded] = useState({});

  if (loading) return <p className="vi-dashboard-empty">جاري تحميل القواعد...</p>;
  if (groups.length === 0) return <p className="vi-dashboard-empty">لا توجد قواعد شحن بعد — أضف أول قاعدة من الزر أعلاه</p>;

  return (
    <div className="vi-shipping-groups">
      {groups.map((group, gi) => {
        const isExpanded = expanded[gi];
        const allActive = group.rules.every((r) => r.is_active);
        const routeCount = group.rules.length;

        return (
          <div key={group.total_rate + '-' + group.rider_rate + '-' + gi} className={`vi-shipping-group${allActive ? '' : ' vi-shipping-group--inactive'}`}>
            <div className="vi-shipping-group__header">
              <div className="vi-shipping-group__summary">
                <div className="vi-shipping-group__rate">
                  <span className="vi-shipping-group__rate-value">{Number(group.total_rate).toFixed(2)}</span>
                  <span className="vi-shipping-group__rate-currency">{currencySymbol}</span>
                  {ridersEnabled ? (
                    <span className="vi-shipping-group__breakdown">
                      مندوب: {Number(group.rider_rate).toFixed(2)} · شركة: {Number(group.company_rate).toFixed(2)}
                      {' · '}مرتجع: {Number(group.merchant_return_fee || 0).toFixed(2)}
                    </span>
                  ) : (
                    <span className="vi-shipping-group__breakdown">
                      إيراد الشركة: {Number(group.company_rate).toFixed(2)}
                      {' · '}مرتجع: {Number(group.merchant_return_fee || 0).toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="vi-shipping-group__meta">
                  <span className="vi-badge vi-badge--primary">{routeCount} {routeCount === 1 ? 'وجهة' : 'وجهات'}</span>
                  <span className={`vi-badge${allActive ? '' : ' vi-badge--muted'}`}>
                    {allActive ? 'مفعلة' : 'معطلة'}
                  </span>
                </div>
              </div>
              <div className="vi-shipping-group__actions">
                {canEdit && <button type="button" className="vi-btn vi-btn-sm vi-btn-outline" onClick={() => onEditGroup(group)}>تعديل السعر</button>}
                {canEdit && <button type="button" className="vi-btn vi-btn-sm vi-btn-outline" onClick={() => onToggleGroup(group)}>
                  {allActive ? 'تعطيل الكل' : 'تفعيل الكل'}
                </button>}
                {canEdit && <button type="button" className="vi-btn vi-btn-sm vi-btn-danger-outline" onClick={() => onDeleteGroup(group)}>حذف الكل</button>}
                <button type="button" className="vi-btn vi-btn-sm vi-btn-ghost" onClick={() => setExpanded((p) => ({ ...p, [gi]: !p[gi] }))}>
                  {isExpanded ? '▲ إخفاء' : '▼ عرض الوجهات'}
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="vi-shipping-group__routes">
                {group.rules.map((rule) => {
                  const srcText = rule.merchant_name || rule.source_city_name || 'أي مصدر';
                  const srcRegion = rule.source_region_name;
                  const dstText = rule.destination_city_name || '—';
                  const dstRegion = rule.destination_region_name;

                  return (
                    <div key={rule.id} className="vi-shipping-route">
                      <div className="vi-shipping-route__path">
                        <span className="vi-shipping-route__source">
                          {rule.merchant_name ? '🏪' : '📍'} {srcText}
                          {srcRegion ? <span className="vi-shipping-route__region"> · {srcRegion}</span> : ''}
                        </span>
                        <span className="vi-shipping-route__arrow">←</span>
                        <span className="vi-shipping-route__dest">
                          📍 {dstText}
                          {dstRegion ? <span className="vi-shipping-route__region"> · {dstRegion}</span> : ''}
                        </span>
                      </div>
                      <div className="vi-shipping-route__meta">
                        <span className="vi-badge vi-badge--muted">{rule.source_type === 'any' ? 'عام' : rule.source_type === 'merchant' ? 'تاجر' : 'مدينة'}</span>
                        <span className={`vi-badge${rule.is_active ? '' : ' vi-badge--muted'}`}>
                          {rule.is_active ? 'نشط' : 'متوقف'}
                        </span>
                      </div>
                      <div className="vi-shipping-route__actions">
                        {canEdit && <button type="button" className="vi-btn vi-btn-sm vi-btn-outline" onClick={() => onEdit(rule)}>تعديل</button>}
                        {canEdit && <button type="button" className="vi-btn vi-btn-sm vi-btn-outline" onClick={() => onToggle(rule)}>
                          {rule.is_active ? 'إيقاف' : 'تشغيل'}
                        </button>}
                        {canEdit && <button type="button" className="vi-btn vi-btn-sm vi-btn-danger-outline" onClick={() => onDelete(rule)}>حذف</button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function GroupEditModal({ group, form, setForm, saving, error, ridersEnabled = true, onClose, onSubmit }) {
  const total = Number(form.total_rate || 0);
  const rider = ridersEnabled ? Number(form.rider_rate || 0) : 0;
  const companyRate = Math.max(0, total - rider);

  return (
    <FormDialog
      open
      onClose={saving ? () => {} : onClose}
      title="تعديل مجموعة تسعير"
      subtitle={`${group.rules.length} ${group.rules.length === 1 ? 'وجهة' : 'وجهات'} بنفس السعر`}
      icon="💰"
      maxWidth={560}
      loading={saving}
      footer={(
        <>
          <DialogButton variant="ghost" onClick={onClose} disabled={saving}>إلغاء</DialogButton>
          <DialogButton variant="primary" type="submit" form="group-edit-form" loading={saving}>تحديث المجموعة</DialogButton>
        </>
      )}
    >
      <form id="group-edit-form" className="vi-form-dialog__form" onSubmit={onSubmit}>
        {error && <div className="vi-alert vi-alert--error" style={{ marginBottom: 12 }}>{error}</div>}
        <FormSection title="الوجهات المشمولة" icon="🗺️">
          <FormField full>
            <div className="vi-shipping-route-list">
              {group.rules.map((r) => (
                <div key={r.id} className="vi-shipping-route-list__item">
                  {r.merchant_name || r.source_city_name || 'أي مصدر'}
                  {r.source_region_name ? ` (${r.source_region_name})` : ''}
                  <span className="vi-shipping-route-list__arrow">←</span>
                  {r.destination_city_name || '—'}
                  {r.destination_region_name ? ` (${r.destination_region_name})` : ''}
                </div>
              ))}
            </div>
          </FormField>
        </FormSection>
        <FormSection title="التسعير" icon="💵">
          <div className="vi-shipping-rate-grid vi-shipping-rate-grid--dialog">
            <RateField label="إجمالي الشحن" value={form.total_rate} onChange={(v) => setForm((p) => ({ ...p, total_rate: v }))} />
            {ridersEnabled && (
              <RateField label="تكلفة المندوب" value={form.rider_rate} onChange={(v) => setForm((p) => ({ ...p, rider_rate: v }))} />
            )}
            <FormField>
              <label className="vi-label">{ridersEnabled ? 'تكلفة الشركة' : 'إيراد الشركة'}</label>
              <div className="vi-form-locked" dir="ltr">{companyRate.toFixed(2)}</div>
            </FormField>
            <RateField
              label="رسوم المرتجع على التاجر"
              value={form.merchant_return_fee ?? '0'}
              onChange={(v) => setForm((p) => ({ ...p, merchant_return_fee: v }))}
            />
          </div>
          <FormField full>
            <label className="vi-shipping-toggle__label" style={{ marginTop: 4 }}>
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked ? 1 : 0 }))}
              />
              <span>جميع القواعد في المجموعة مفعلة</span>
            </label>
          </FormField>
        </FormSection>
      </form>
    </FormDialog>
  );
}

function RuleModal({
  isMobile, editing, error, saving, ruleForm, setRuleForm,
  merchantList, countries, sourceCities, destinationCities,
  companyCountryId, companyCountryName, sourceRegions, destinationRegions,
  ridersEnabled = true, onSourceCountryChange, onLoadSourceRegions, onLoadDestinationRegions,
  onCreateSourceRegion, onCreateDestinationRegion, onClose, onSubmit,
}) {
  const ruleCompanyRate = ridersEnabled
    ? Math.max(0, Number(ruleForm.total_rate || 0) - Number(ruleForm.rider_rate || 0)).toFixed(2)
    : Number(ruleForm.total_rate || 0).toFixed(2);
  const showSourceRegions = ruleForm.source_type !== 'any';
  const sourceCityCount = (ruleForm.source_city_ids || []).length;
  const sourceCityReady = ruleForm.source_type === 'city'
    ? sourceCityCount > 0
    : ruleForm.source_type === 'merchant'
      ? !!ruleForm.merchant_id
      : false;
  const sourceRegionsEnabled = showSourceRegions && sourceCityReady && (
    ruleForm.source_type !== 'city' || sourceCityCount === 1
  );
  const destCityCount = (ruleForm.destination_city_ids || []).length;
  const destCityReady = destCityCount > 0;
  const destRegionsEnabled = destCityReady && destCityCount === 1;

  async function handleSourceCountryChange(countryId) {
    setRuleForm((prev) => ({
      ...prev,
      source_country_id: countryId,
      source_city_ids: [],
      source_region_ids: [],
    }));
    onLoadSourceRegions(null);
    await onSourceCountryChange(countryId);
  }

  function handleSourceCitiesChange(ids) {
    setRuleForm((prev) => ({
      ...prev,
      source_city_ids: ids,
      source_region_ids: ids.length === 1 ? prev.source_region_ids : [],
    }));
    onLoadSourceRegions(ids.length === 1 ? ids[0] : null);
  }

  function handleDestinationCitiesChange(ids) {
    setRuleForm((prev) => ({
      ...prev,
      destination_city_ids: ids,
      destination_region_ids: ids.length === 1 ? prev.destination_region_ids : [],
    }));
    onLoadDestinationRegions(ids.length === 1 ? ids[0] : null);
  }

  return (
    <FormDialog
      open
      onClose={saving ? () => {} : onClose}
      title={editing ? 'تعديل قاعدة الشحن' : 'إضافة قاعدة شحن'}
      subtitle={editing ? 'تعديل مسار التسعير الحالي' : 'حدّد المصدر والوجهة والأسعار'}
      icon="🚚"
      maxWidth={isMobile ? undefined : 780}
      loading={saving}
      footer={(
        <>
          <DialogButton variant="ghost" onClick={onClose} disabled={saving}>إلغاء</DialogButton>
          <DialogButton variant="primary" type="submit" form="rule-form" loading={saving}>
            {editing ? 'حفظ التعديلات' : 'إضافة القاعدة'}
          </DialogButton>
        </>
      )}
    >
      <form id="rule-form" className="vi-form-dialog__form" onSubmit={onSubmit}>
        {error && <div className="vi-alert vi-alert--error" style={{ marginBottom: 12 }}>{error}</div>}

        <FormSection title="المصدر" icon="📍" id="rule-source">
          <FormField>
            <label className="vi-label">نوع المصدر</label>
            <select
              className="vi-input"
              value={ruleForm.source_type}
              onChange={(e) => {
                const value = e.target.value;
                setRuleForm((prev) => ({
                  ...prev,
                  source_type: value,
                  merchant_id: value === 'merchant' ? prev.merchant_id : '',
                  source_country_id: value === 'city'
                    ? (prev.source_country_id || (companyCountryId ? String(companyCountryId) : ''))
                    : '',
                  source_city_ids: value === 'city' ? prev.source_city_ids : [],
                  source_region_ids: [],
                }));
                if (value === 'merchant') {
                  const merchant = merchantList.find((m) => String(m.id) === String(ruleForm.merchant_id));
                  onLoadSourceRegions(merchant?.city_id);
                } else if (value === 'city') {
                  const countryId = ruleForm.source_country_id || (companyCountryId ? String(companyCountryId) : '');
                  if (countryId) onSourceCountryChange(countryId);
                  onLoadSourceRegions(
                    ruleForm.source_city_ids?.length === 1 ? ruleForm.source_city_ids[0] : null,
                  );
                } else {
                  onLoadSourceRegions(null);
                }
              }}
            >
              <option value="any">عام — أي مصدر</option>
              <option value="city">مدينة محددة</option>
              <option value="merchant">تاجر محدد</option>
            </select>
          </FormField>
          {ruleForm.source_type === 'merchant' && (
            <FormField>
              <label className="vi-label">التاجر</label>
              <SearchableSelect
                value={ruleForm.merchant_id}
                onChange={(val) => {
                  const merchant = merchantList.find((m) => String(m.id) === String(val));
                  setRuleForm((prev) => ({ ...prev, merchant_id: val, source_region_ids: [] }));
                  onLoadSourceRegions(merchant?.city_id);
                }}
                options={merchantList.map((m) => ({ value: m.id, label: m.name }))}
                placeholder="اختر التاجر"
              />
              <p className="vi-form-hint">مدينة التاجر تُستخدم تلقائياً كمصدر</p>
            </FormField>
          )}
          {ruleForm.source_type === 'city' && (
            <>
              <FormField>
                <label className="vi-label">دولة المصدر</label>
                <SearchableSelect
                  value={ruleForm.source_country_id}
                  onChange={handleSourceCountryChange}
                  options={countries.map((c) => ({
                    value: c.id,
                    label: c.name_ar || c.name,
                  }))}
                  placeholder="اختر الدولة"
                />
                <p className="vi-form-hint">يمكن اختيار دولة مختلفة عن دولة الشركة عند وجود تجار في دول أخرى</p>
              </FormField>
              <FormField full>
                <RegionCheckboxPicker
                  label="مدن المصدر"
                  hint="اختر مدينة أو أكثر — تُنشأ قاعدة لكل مدينة مصدر"
                  regions={sourceCities}
                  selectedIds={ruleForm.source_city_ids}
                  onChange={handleSourceCitiesChange}
                  disabled={!ruleForm.source_country_id}
                  disabledMessage="اختر دولة المصدر أولاً"
                  createPlaceholder="ابحث عن مدينة…"
                  emptyMessage="لا توجد مدن لهذه الدولة"
                />
              </FormField>
            </>
          )}
        </FormSection>

        <FormSection title="الوجهة" icon="🎯" id="rule-destination">
          <FormField full>
            <RegionCheckboxPicker
              label={`مدن الوجهة${companyCountryName ? ` (${companyCountryName})` : ''}`}
              hint="اختر مدينة أو أكثر — تُنشأ قاعدة لكل مدينة وجهة"
              regions={destinationCities}
              selectedIds={ruleForm.destination_city_ids}
              onChange={handleDestinationCitiesChange}
              disabled={!destinationCities.length}
              disabledMessage={companyCountryName ? `لا توجد مدن لـ ${companyCountryName}` : 'لا توجد مدن متاحة'}
              createPlaceholder="ابحث عن مدينة…"
              emptyMessage="لا توجد مدن لهذه الدولة"
            />
          </FormField>
        </FormSection>

        <FormSection title="المناطق" icon="🗺️" id="rule-regions">
          <div className="vi-shipping-regions-grid">
            {showSourceRegions ? (
              <RegionCheckboxPicker
                label="مناطق المصدر"
                hint={
                  ruleForm.source_type === 'city' && sourceCityCount > 1
                    ? 'متاحة عند اختيار مدينة مصدر واحدة فقط — تُطبَّق القاعدة على المدن كاملة'
                    : 'اتركها فارغة لتطبيق القاعدة على المدينة كاملة'
                }
                regions={sourceRegions}
                selectedIds={ruleForm.source_region_ids}
                onChange={(ids) => setRuleForm((prev) => ({ ...prev, source_region_ids: ids }))}
                disabled={!sourceRegionsEnabled}
                disabledMessage={
                  ruleForm.source_type === 'merchant'
                    ? 'اختر التاجر أولاً'
                    : ruleForm.source_type === 'city' && sourceCityCount > 1
                      ? 'اختر مدينة مصدر واحدة لتحديد المناطق'
                      : 'اختر مدينة المصدر أولاً'
                }
                allowCreate
                onCreate={onCreateSourceRegion}
                emptyMessage="لا توجد مناطق لهذه المدينة بعد"
              />
            ) : (
              <div className="vi-shipping-region-placeholder">
                المصدر «عام» — لا حاجة لتحديد مناطق مصدر
              </div>
            )}
            <RegionCheckboxPicker
              label="مناطق الوجهة"
              hint={
                destCityCount > 1
                  ? 'متاحة عند اختيار مدينة وجهة واحدة فقط — تُطبَّق القاعدة على المدن كاملة'
                  : 'اختر منطقة أو أكثر — تُنشأ قاعدة لكل منطقة'
              }
              regions={destinationRegions}
              selectedIds={ruleForm.destination_region_ids}
              onChange={(ids) => setRuleForm((prev) => ({ ...prev, destination_region_ids: ids }))}
              disabled={!destRegionsEnabled}
              disabledMessage={
                destCityCount > 1
                  ? 'اختر مدينة وجهة واحدة لتحديد المناطق'
                  : 'اختر مدينة الوجهة أولاً'
              }
              allowCreate
              onCreate={onCreateDestinationRegion}
              emptyMessage="لا توجد مناطق لهذه المدينة بعد"
            />
          </div>
        </FormSection>

        <FormSection title="التسعير" icon="💰" id="rule-pricing">
          <div className="vi-shipping-rate-grid vi-shipping-rate-grid--dialog">
            <RateField label="إجمالي الشحن" value={ruleForm.total_rate} onChange={(v) => setRuleForm((prev) => ({ ...prev, total_rate: v }))} />
            {ridersEnabled && (
              <RateField label="تكلفة المندوب" value={ruleForm.rider_rate} onChange={(v) => setRuleForm((prev) => ({ ...prev, rider_rate: v }))} />
            )}
            <FormField>
              <label className="vi-label">{ridersEnabled ? 'تكلفة الشركة' : 'إيراد الشركة'}</label>
              <div className="vi-form-locked" dir="ltr">{ruleCompanyRate}</div>
            </FormField>
            <RateField
              label="رسوم المرتجع على التاجر"
              value={ruleForm.merchant_return_fee ?? '0'}
              onChange={(v) => setRuleForm((prev) => ({ ...prev, merchant_return_fee: v }))}
            />
          </div>
          <p className="vi-form-hint" style={{ marginTop: 4 }}>
            القيمة الافتراضية من إعدادات الشحن العامة — يمكنك تعديلها لهذه القاعدة فقط
          </p>
          <FormField full>
            <label className="vi-shipping-toggle__label">
              <input
                type="checkbox"
                checked={!!ruleForm.is_active}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
              />
              <span>قاعدة مفعلة</span>
            </label>
          </FormField>
        </FormSection>
      </form>
    </FormDialog>
  );
}
